import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle, XCircle, Clock, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/contexts/LocaleContext";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

interface SubmitResolutionModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: {
    id: string;
    title: string;
    yes_pool: number;
    no_pool: number;
  } | null;
  onResolutionSubmitted: () => void;
}

const SubmitResolutionModal = ({ 
  isOpen, 
  onClose, 
  event, 
  onResolutionSubmitted 
}: SubmitResolutionModalProps) => {
  const { locale } = useLocale();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  if (!event) return null;

  const handleSubmitResolution = async (outcome: boolean) => {
    if (!user) return;
    
    setIsLoading(true);

    try {
      // Calculate dispute deadline (24 hours from now)
      const disputeDeadline = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
      
      // Insert the pending resolution with dispute deadline
      const { error } = await supabase
        .from("pending_resolutions")
        .insert({
          event_id: event.id,
          proposed_outcome: outcome,
          submitted_by: user.id,
          dispute_deadline: disputeDeadline,
        });

      if (error) throw error;

      // Fetch username for notification
      const { data: profileData } = await supabase
        .from("profiles")
        .select("username")
        .eq("user_id", user.id)
        .single();

      // Send notification to admins (fire and forget)
      supabase.functions.invoke("notify-admins", {
        body: {
          event_title: event.title,
          submitted_by_username: profileData?.username || "Unknown",
          proposed_outcome: outcome,
        },
      }).catch(err => console.error("Failed to notify admins:", err));

      toast.success(
        locale.language === "pt"
          ? "Resolução enviada para aprovação!"
          : "Resolution submitted for approval!",
        {
          description:
            locale.language === "pt"
              ? "Um administrador irá revisar sua solicitação."
              : "An admin will review your request.",
        }
      );

      onResolutionSubmitted();
      onClose();
    } catch (error: any) {
      if (error.code === "23505") {
        toast.error(
          locale.language === "pt"
            ? "Já existe uma resolução pendente para este evento."
            : "A pending resolution already exists for this event."
        );
      } else {
        toast.error(error.message || "Error submitting resolution");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md z-50"
          >
            <div className="glass-card p-6 m-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-foreground">
                  {locale.language === "pt" ? "Solicitar Resolução" : "Submit Resolution"}
                </h2>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-muted transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                {event.title}
              </p>

              <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/10 border border-primary/20 mb-6">
                <Clock size={18} className="text-primary" />
                <span className="text-sm text-primary">
                  {locale.language === "pt"
                    ? "Resolução será aprovada automaticamente em 24h se não houver disputas."
                    : "Resolution will auto-approve in 24h if no disputes."}
                </span>
              </div>

              <div className="mb-6">
                <p className="text-sm text-muted-foreground mb-2">
                  {locale.language === "pt" ? "Pools atuais:" : "Current pools:"}
                </p>
                <div className="flex gap-4">
                  <div className="flex-1 p-3 rounded-lg bg-success/10 text-center">
                    <p className="text-success font-bold">
                      {event.yes_pool.toLocaleString()}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {locale.language === "pt" ? "SIM" : "YES"}
                    </p>
                  </div>
                  <div className="flex-1 p-3 rounded-lg bg-destructive/10 text-center">
                    <p className="text-destructive font-bold">
                      {event.no_pool.toLocaleString()}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {locale.language === "pt" ? "NÃO" : "NO"}
                    </p>
                  </div>
                </div>
              </div>

              <p className="text-sm font-medium text-foreground mb-3">
                {locale.language === "pt" ? "Qual foi o resultado?" : "What was the outcome?"}
              </p>

              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="success"
                  onClick={() => handleSubmitResolution(true)}
                  disabled={isLoading}
                  className="flex items-center gap-2"
                >
                  <CheckCircle size={18} />
                  {locale.language === "pt" ? "SIM Ganhou" : "YES Won"}
                </Button>
                <Button
                  variant="danger"
                  onClick={() => handleSubmitResolution(false)}
                  disabled={isLoading}
                  className="flex items-center gap-2"
                >
                  <XCircle size={18} />
                  {locale.language === "pt" ? "NÃO Ganhou" : "NO Won"}
                </Button>
              </div>

              <Button
                variant="ghost"
                onClick={onClose}
                className="w-full mt-3"
                disabled={isLoading}
              >
                {locale.language === "pt" ? "Cancelar" : "Cancel"}
              </Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SubmitResolutionModal;
